// src/hooks/useAuth.ts
import { useCallback, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  fetchUser,
  logout as logoutAction,
  selectAuthState,
  selectCustomer,
  selectCustomerId,
  selectIsGuest,
  selectIsLoggedIn,
  setCustomer as setCustomerAction,
  setDeviceId as setDeviceIdAction,
  setToken as setTokenAction,
  TokenModel,
} from '../store/authSlice';
import { CustomerModel } from '../models';
import { useStore } from './useStore';

export function useAuth() {
  const dispatch = useAppDispatch();
  const { storeId } = useStore();

  const { deviceId, token, isUserLoading, hasFetchedUser } = useAppSelector(selectAuthState);
  const customer = useAppSelector(selectCustomer);
  const customerId = useAppSelector(selectCustomerId);
  const isLoggedIn = useAppSelector(selectIsLoggedIn);
  const isGuest = useAppSelector(selectIsGuest);

  useEffect(() => {
    if (
      !hasFetchedUser &&           // 👈 only if not already fetched
      isLoggedIn &&
      !isGuest &&
      token &&
      storeId
    ) {
      dispatch(fetchUser());
    }
  }, [dispatch, hasFetchedUser, isLoggedIn, isGuest, token, storeId]);

  const setDeviceId = useCallback(
    (value: string | null) => {
      dispatch(setDeviceIdAction(value));
    },
    [dispatch]
  );

  const setToken = useCallback(
    (value: TokenModel | null) => {
      dispatch(setTokenAction(value));
    },
    [dispatch]
  );

  const setCustomer = useCallback(
    (value: CustomerModel) => {
      dispatch(setCustomerAction(value));
    },
    [dispatch]
  );

  const logout = useCallback(() => {
    dispatch(logoutAction());
  }, [dispatch]);

  return {
    deviceId,
    setDeviceId,
    token,
    setToken,
    customerId,
    customer,
    setCustomer,
    isLoggedIn,
    isGuest,
    logout,
    isUserLoading,
  };
}